import React, { useState, useEffect } from 'react';
import './EscalationSuccess.css';

/**
 * EscalationSuccess Component
 * Shows success message after escalation submission
 * 
 * @param {Object} props
 * @param {Object} props.ticket - Ticket information
 * @param {Function} props.onClose - Close callback
 */
const EscalationSuccess = ({ ticket, onClose }) => {
  const [copyButtonText, setCopyButtonText] = useState('Copy');

  /**
   * Copy ticket ID to clipboard
   */
  const copyTicketId = async () => {
    if (!ticket?.ticket_id) return;

    try {
      await navigator.clipboard.writeText(ticket.ticket_id);
      setCopyButtonText('Copied!');
      setTimeout(() => {
        setCopyButtonText('Copy');
      }, 2000);
    } catch (error) {
      console.error('Failed to copy ticket ID:', error);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = ticket.ticket_id;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopyButtonText('Copied!');
        setTimeout(() => {
          setCopyButtonText('Copy');
        }, 2000);
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);
        setCopyButtonText('Failed');
        setTimeout(() => {
          setCopyButtonText('Copy');
        }, 2000);
      }
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus trap for accessibility
  useEffect(() => {
    const modal = document.querySelector('.escalation-success-modal');
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    modal.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => modal.removeEventListener('keydown', handleTabKey);
  }, []);

  // Format creation date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return 'Unknown';
    }
  };

  // Get expected response time based on severity
  const getExpectedResponseTime = (severity) => {
    const severityMap = {
      'P1 - Critical': '1-2 hours',
      'P2 - High': '4-8 hours',
      'P3 - Normal': '12-24 hours',
      'P4 - Low': '24-48 hours'
    };
    return severityMap[severity] || '2-24 hours';
  };

  return (
    <div className="escalation-success-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div 
        className="escalation-success-modal" 
        role="dialog" 
        aria-labelledby="success-title" 
        aria-modal="true"
      >
        <div className="success-icon" aria-hidden="true">✅</div>
        <h2 id="success-title">Support Request Submitted</h2>
        <p>Your request has been successfully submitted to our support team.</p>
        
        <div className="ticket-info">
          <label htmlFor="ticket-id-display">Ticket ID:</label>
          <div className="ticket-id">
            <code id="ticket-id-display" aria-describedby="ticket-id-help">
              {ticket?.ticket_id || 'Unknown'}
            </code>
            <button 
              onClick={copyTicketId} 
              className="copy-button"
              aria-label={`Copy ticket ID ${ticket?.ticket_id || ''}`}
              disabled={!ticket?.ticket_id}
            >
              {copyButtonText}
            </button>
          </div>
          <small id="ticket-id-help" className="help-text">
            Save this ticket ID for future reference
          </small>
        </div>

        {/* Additional ticket details */}
        {ticket && (
          <div className="ticket-details">
            <div className="detail-row">
              <span className="detail-label">Issue Type:</span>
              <span className="detail-value">{ticket.issue_type || 'Not specified'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Severity:</span>
              <span className="detail-value">{ticket.severity || 'Not specified'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Submitted:</span>
              <span className="detail-value">{formatDate(ticket.created_at)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className="detail-value status-badge">{ticket.status || 'Pending'}</span>
            </div>
          </div>
        )}
        
        <div className="next-steps">
          <h3>What happens next?</h3>
          <ul>
            <li>Our support team will review your request</li>
            <li>You'll receive an email confirmation shortly</li>
            <li>We'll contact you via your preferred contact method</li>
            <li>
              Expected response time: {' '}
              <strong>{getExpectedResponseTime(ticket?.severity)}</strong>
              {' '} depending on severity
            </li>
          </ul>
        </div>

        {/* Contact information */}
        <div className="contact-info">
          <h4>Need immediate assistance?</h4>
          <p>
            For urgent matters, you can also reach us at{' '}
            <a href="mailto:support@borisbot.com" className="contact-link">
              support@borisbot.com
            </a>
            {' '}or call{' '}
            <a href="tel:+1-800-BORIS-AI" className="contact-link">
              1-800-BORIS-AI
            </a>
          </p>
        </div>
        
        <button onClick={onClose} className="close-button-primary">
          Continue Chat
        </button>
      </div>
    </div>
  );
};

export default EscalationSuccess;
